/**
 * searchExcerpt — the visible fragment of a search result.
 *
 * Founder email 2026-10-25 (card vN5IhlgoFXTb): "Search in thread doesn't
 * actually return words searched." The archive search itself matched
 * correctly, but a result row printed the FIRST 120 characters of the message
 * — so a word that sat late in a long message was matched by the server and
 * then not visible in the row. The list then read as if it had returned
 * unrelated messages.
 *
 * This windows the snippet on the FIRST case-insensitive occurrence of the
 * query (a little lead-in for context) and hands the matched run back
 * separately so the caller can mark it. The match is case-insensitive for the
 * same reason the server's is: `instr(lower(text), lower(q))`.
 */

export type SearchExcerpt = {
  /** Text before the match, prefixed with an ellipsis on a deliberate cut. */
  before: string;
  /** The matched run, exactly as it appears in the message. Empty on no match. */
  match: string;
  /** Text after the match, suffixed with an ellipsis on a deliberate cut. */
  after: string;
};

/** Characters of lead-in kept before the match, so the row has context. */
const LEAD = 36;

/** Total characters of message text the row shows. */
export const EXCERPT_CHARS = 120;

export function searchExcerpt(text: string, query: string, max = EXCERPT_CHARS): SearchExcerpt {
  const body = text ?? '';
  const q = (query ?? '').trim().toLowerCase();
  const flat = body.toLowerCase();
  const at = q ? flat.indexOf(q) : -1;

  // No match (or no query): show the head, which is all a row can honestly
  // claim when the term is not visible in it.
  if (at < 0) {
    return {
      before: body.length > max ? body.slice(0, max) + '…' : body,
      match: '',
      after: '',
    };
  }

  // `toLowerCase()` can change length for a handful of code points, so clamp
  // the match end against the original string rather than trusting `at + q.length`.
  const matchLen = Math.min(q.length, Math.max(0, body.length - at));
  const start = Math.max(0, at - LEAD);
  const head = body.slice(start, at);
  const match = body.slice(at, at + matchLen);
  const tailBudget = Math.max(0, max - head.length - match.length);
  const tail = body.slice(at + matchLen, at + matchLen + tailBudget);

  return {
    before: (start > 0 ? '…' : '') + head,
    match,
    after: tail + (at + matchLen + tail.length < body.length ? '…' : ''),
  };
}
